const STORAGE_KEY = "mock_movies_v1";

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeBool = (val) => val === true || val === 1 || val === "1";

function createResponse(data, status = 200) {
  return Promise.resolve({ status, data });
}

function loadMovies() {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (error) {
    // reset below
  }

  return [];
}

function saveMovies(rows) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

function nextMovieId(rows) {
  return (
    rows.reduce((max, row) => {
      const id = Number(row.movie_id) || 0;
      return id > max ? id : max;
    }, 0) + 1
  );
}

function nextUnique(rows) {
  const maxUnique = rows.reduce((max, row) => {
    const unique = Number((row.movie_information && row.movie_information.unique) || row.unique) || 0;
    return unique > max ? unique : max;
  }, 1000);
  return String(maxUnique + 1);
}

function normalizeSort(sort) {
  if (!sort) return { field: "title", order: "asc" };
  if (String(sort).includes("^")) {
    const [field, order] = String(sort).split("^");
    return { field: field || "title", order: (order || "asc").toLowerCase() };
  }
  return { field: String(sort), order: "asc" };
}

function mapForList(movie) {
  const movieInfo = movie.movie_information || {};
  return {
    ...movie,
    title: movieInfo.title || movie.title || "",
    international_title:
      movieInfo.international_title || movie.international_title || "",
    unique: movieInfo.unique || movie.unique || "",
    year: movieInfo.year || movie.year || "",
    isan_number: movieInfo.isan_number || movie.isan_number || "",
    website_visible:
      typeof movieInfo.website_visible !== "undefined"
        ? movieInfo.website_visible
        : movie.website_visible,
    archived: normalizeBool(movie.archived),
    draft: normalizeBool(movie.draft),
  };
}

function mapPayloadToRecord(payload, existing = {}) {
  const now = new Date().toISOString();
  const movieInfo = clone(payload.movie_information || {});

  return {
    ...existing,
    ...clone(payload),
    movie_information: {
      ...(existing.movie_information || {}),
      ...movieInfo,
    },
    movie_material_digital: clone(payload.material_digital_arry || payload.movie_material_digital || []),
    movie_material_printed: clone(payload.material_printed_arry || payload.movie_material_printed || []),
    movie_distributors: clone(payload.movie_distributors || []),
    movie_product: clone(payload.movie_product || []),
    movie_release: clone(payload.movie_release || []),
    movie_synopsis: clone(payload.movie_synopsis || []),
    trailer_links: clone(payload.trailer_links || []),
    additional_langs: clone(payload.additional_langs || []),
    additional_langs_info: clone(payload.additional_langs_info || []),
    draft: normalizeBool(payload.draft),
    archived: normalizeBool(existing.archived),
    website_visible:
      typeof movieInfo.website_visible !== "undefined"
        ? movieInfo.website_visible
        : existing.website_visible,
    title: movieInfo.title || existing.title || "",
    international_title:
      movieInfo.international_title || existing.international_title || "",
    unique: movieInfo.unique || existing.unique || "",
    year: movieInfo.year || existing.year || "",
    isan_number: movieInfo.isan_number || existing.isan_number || "",
    updatedAt: now,
    updatedBy_name: "Mock User",
    createdAt: existing.createdAt || now,
    createdBy_name: existing.createdBy_name || "Mock User",
  };
}

export const getAllMovie = async ({
  limit = 20,
  page = 1,
  search = "",
  archived,
  sort,
  draft,
} = {}) => {
  const query = String(search || "").trim().toLowerCase();
  const hasArchived = typeof archived !== "undefined";
  const hasDraft = typeof draft !== "undefined";
  const archivedValue = normalizeBool(archived);
  const draftValue = normalizeBool(draft);
  const { field, order } = normalizeSort(sort);
  const multiplier = order === "desc" ? -1 : 1;

  let rows = loadMovies().map(mapForList).filter((row) => {
    if (hasArchived && normalizeBool(row.archived) !== archivedValue) return false;
    if (hasDraft && normalizeBool(row.draft) !== draftValue) return false;

    if (!query) return true;
    const blob = [row.title, row.international_title, row.unique, row.year, row.isan_number]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return blob.includes(query);
  });

  rows = rows.sort((a, b) => {
    const av = (a[field] || "").toString().toLowerCase();
    const bv = (b[field] || "").toString().toLowerCase();
    if (av < bv) return -1 * multiplier;
    if (av > bv) return 1 * multiplier;
    return 0;
  });

  const perPage = Number(limit) || 20;
  const currentPage = Number(page) || 1;
  const start = (currentPage - 1) * perPage;
  const paged = rows.slice(start, start + perPage);

  return createResponse({
    status: true,
    message: "Success",
    data: {
      data: paged,
      count: rows.length,
      pagination: {
        total: rows.length,
        page: currentPage,
        limit: perPage,
      },
    },
  });
};

export const getSingleMovie = async (id) => {
  const row = loadMovies().find((movie) => Number(movie.movie_id) === Number(id));

  if (!row) {
    return createResponse(
      {
        status: false,
        message: "Movie not found",
        data: null,
      },
      404
    );
  }

  const normalized = mapPayloadToRecord(row, row);

  return createResponse({
    status: true,
    message: "Success",
    data: {
      movie_information: clone(normalized.movie_information || {}),
      additional_langs: clone(normalized.additional_langs || []),
      additional_langs_info: clone(normalized.additional_langs_info || []),
      movie_distributors: clone(normalized.movie_distributors || []),
      movie_product: clone(normalized.movie_product || []),
      movie_release: clone(normalized.movie_release || []),
      movie_synopsis: clone(normalized.movie_synopsis || []),
      movie_material_digital: clone(normalized.movie_material_digital || []),
      movie_material_printed: clone(normalized.movie_material_printed || []),
      trailer_links: clone(normalized.trailer_links || []),
      movie_id: normalized.movie_id,
    },
  });
};

export const saveMovie = async (payload = {}) => {
  const rows = loadMovies();
  const providedId = Number(payload.movieID);
  const isUpdate = providedId && !Number.isNaN(providedId);

  if (isUpdate) {
    const index = rows.findIndex((row) => Number(row.movie_id) === providedId);
    if (index < 0) {
      return createResponse({
        status: false,
        message: "Movie not found",
      }, 404);
    }

    rows[index] = mapPayloadToRecord(payload, rows[index]);
    rows[index].movie_id = providedId;
    if (!rows[index].movie_information.unique) {
      rows[index].movie_information.unique = rows[index].unique || nextUnique(rows);
    }
    rows[index].unique = rows[index].movie_information.unique;

    saveMovies(rows);
    return createResponse({
      status: true,
      message: "Movie Saved Successfully",
      data: { movie_id: providedId },
    });
  }

  const movieId = nextMovieId(rows);
  const record = mapPayloadToRecord(payload, {});
  record.movie_id = movieId;
  record.movie_information.unique =
    record.movie_information.unique || payload.newMovieUniqueId || nextUnique(rows);
  record.unique = record.movie_information.unique;

  rows.unshift(record);
  saveMovies(rows);

  return createResponse({
    status: true,
    message: "Movie Saved Successfully",
    data: { movie_id: movieId },
  });
};

export const saveMovieWebsiteVisible = async (payload = {}) => {
  const movieId = Number(payload.movie_id);
  const rows = loadMovies();
  const index = rows.findIndex((row) => Number(row.movie_id) === movieId);

  if (index < 0) {
    return createResponse({ status: false, message: "Movie not found" }, 404);
  }

  const row = rows[index];
  row.movie_information = row.movie_information || {};
  row.movie_information.website_visible = payload.website_visible;
  row.website_visible = payload.website_visible;
  row.updatedAt = new Date().toISOString();
  row.updatedBy_name = "Mock User";

  saveMovies(rows);
  return createResponse({ status: true, message: "Updated" });
};

export const getNewMovieUnique = async () => {
  const unique = nextUnique(loadMovies());
  return createResponse({
    status: true,
    message: "Success",
    data: { unique },
  });
};

export const archiveMovie = async (movie_id) => {
  const rows = loadMovies();
  const index = rows.findIndex((row) => Number(row.movie_id) === Number(movie_id));
  if (index < 0) {
    return createResponse({ status: false, message: "Movie not found" }, 404);
  }

  rows[index].archived = true;
  rows[index].updatedAt = new Date().toISOString();
  rows[index].updatedBy_name = "Mock User";
  saveMovies(rows);

  return createResponse({
    status: true,
    message: "Movie Archived",
    data: { movie_id: Number(movie_id) },
  });
};

export const unArchiveMovie = async (movie_id) => {
  const rows = loadMovies();
  const index = rows.findIndex((row) => Number(row.movie_id) === Number(movie_id));
  if (index < 0) {
    return createResponse({ status: false, message: "Movie not found" }, 404);
  }

  rows[index].archived = false;
  rows[index].updatedAt = new Date().toISOString();
  rows[index].updatedBy_name = "Mock User";
  saveMovies(rows);

  return createResponse({
    status: true,
    message: "Movie UnArchived",
    data: { movie_id: Number(movie_id) },
  });
};

export const getMovieLogs = async () =>
  createResponse({
    status: true,
    message: "Success",
    data: [],
  });
